import { Box, Stack, Text, HStack, Badge, Icon } from "@chakra-ui/react";
import { Camera, PenLine, User, Users } from "lucide-react";
import { Modal } from "@/components/Modal";
import { fieldTypeMeta } from "@/lib/templateFields";
import { colors } from "@/theme/colors";

const boxStyle = { border: "1px solid", borderColor: colors.border, borderRadius: "8px", bg: colors.white };

const Pills = ({ options }) => (
  <HStack gap="2" flexWrap="wrap">
    {options.map((o) => (
      <Box key={o} px="4" py="1.5" {...boxStyle} fontSize="sm" color={colors.textSecondary}>{o}</Box>
    ))}
  </HStack>
);

const OptionRows = ({ options, multi, includeOther }) => (
  <Stack gap="1.5">
    {options.map((o, i) => (
      <HStack key={`${i}-${o}`} gap="2">
        <Box w="14px" h="14px" borderRadius={multi ? "3px" : "full"} border="2px solid" borderColor={colors.border} flexShrink={0} />
        <Text fontSize="sm" color={colors.textSecondary}>{o}</Text>
      </HStack>
    ))}
    {includeOther && (
      <HStack gap="2">
        <Box w="14px" h="14px" borderRadius={multi ? "3px" : "full"} border="2px solid" borderColor={colors.border} flexShrink={0} />
        <Text fontSize="sm" color={colors.textSecondary} fontStyle="italic">Other…</Text>
      </HStack>
    )}
  </Stack>
);

const CaptureBox = ({ icon, text, dashed }) => (
  <Box {...boxStyle} borderStyle={dashed ? "dashed" : "solid"} py="4" textAlign="center">
    <Icon as={icon} boxSize="5" color={colors.textSecondary} />
    <Text fontSize="xs" color={colors.textSecondary}>{text}</Text>
  </Box>
);

const FieldPreview = ({ field, listsById }) => {
  switch (field.type) {
    case "long_text":
      return <Box {...boxStyle} h="72px" />;
    case "pass_fail_na":
      return <Pills options={["Pass", "Fail", "N/A"]} />;
    case "yes_no_na":
      return <Pills options={["Yes", "No", "N/A"]} />;
    case "yes_no":
      return <Pills options={["Yes", "No"]} />;
    case "checkbox":
      return (
        <HStack gap="2">
          <Box w="16px" h="16px" borderRadius="4px" border="2px solid" borderColor={colors.border} />
          <Text fontSize="sm" color={colors.textSecondary}>Check to confirm</Text>
        </HStack>
      );
    case "dropdown_one":
    case "dropdown_multi": {
      const list = field.list_id ? listsById?.[field.list_id] : null;
      if (!list) return <Text fontSize="xs" color={colors.poppy}>No custom list linked yet.</Text>;
      return (
        <Box>
          <Text fontSize="xs" color={colors.textSecondary} mb="1.5">From list: {list.name}</Text>
          <OptionRows options={list.items} multi={field.type === "dropdown_multi"} includeOther={list.include_other} />
        </Box>
      );
    }
    case "multiple_choice":
      return <OptionRows options={field.options || []} />;
    case "select_worker":
      return <CaptureBox icon={User} text="Pick a worker from your company at submission" dashed />;
    case "select_workers":
      return <CaptureBox icon={Users} text="Pick one or more workers at submission" dashed />;
    case "photo":
      return <CaptureBox icon={Camera} text="Tap to add photo" dashed />;
    case "signature":
      return <CaptureBox icon={PenLine} text="Sign here" />;
    default:
      return <Box {...boxStyle} h="38px" />;
  }
};

export const TemplatePreview = ({ template, lists = [], onClose }) => {
  const listsById = {};
  lists.forEach((l) => { listsById[l.id] = l; });

  return (
    <Modal open onClose={onClose} title={template.title} testId="template-preview-modal">
      <Stack gap="5">
        <HStack gap="2">
          <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2.5">{template.type}</Badge>
          <Badge bg={template.status === "published" ? colors.resolvedBg : colors.pendingBg}
            color={template.status === "published" ? colors.resolvedText : colors.pendingText}
            borderRadius="full" px="2.5" textTransform="capitalize">{template.status}</Badge>
          <Text fontSize="xs" color={colors.textSecondary}>v{template.version}</Text>
        </HStack>
        <Text fontSize="xs" color={colors.textSecondary}>
          Location and submitting employee are captured automatically at submission time.
        </Text>
        {(template.schema?.sections || []).map((sec) => (
          <Box key={sec.id}>
            <HStack mb="3" pb="1.5" borderBottom="2px solid" borderColor={colors.tealTint} gap="2" flexWrap="wrap">
              <Text fontWeight="700" fontSize="sm" textTransform="uppercase" letterSpacing="0.05em" color={colors.tealHover}>
                {sec.title || "Untitled section"}
              </Text>
              {sec.collapsible && <Badge bg={colors.cream} color={colors.textSecondary} borderRadius="full" px="2" fontSize="10px">Collapsible</Badge>}
              {sec.repeatable && <Badge bg={colors.cream} color={colors.textSecondary} borderRadius="full" px="2" fontSize="10px">Repeatable</Badge>}
            </HStack>
            <Stack gap="4">
              {(sec.fields || []).map((fl) =>
                fl.type === "info" ? (
                  <Box key={fl.id} px="3.5" py="2.5" borderRadius="8px" bg={colors.tealTint} borderLeft="3px solid" borderColor={colors.teal}>
                    <Text fontSize="sm" color={colors.textPrimary}>{fl.label || "Info text"}</Text>
                  </Box>
                ) : (
                  <Box key={fl.id}>
                    <HStack gap="1" mb="1.5" flexWrap="wrap">
                      <Text fontSize="sm" fontWeight="500" color={colors.textPrimary}>{fl.label || "Untitled field"}</Text>
                      {fl.required && <Text color={colors.poppy} fontSize="sm">*</Text>}
                      <Text fontSize="xs" color={colors.textSecondary} ml="1">({fieldTypeMeta(fl.type).label})</Text>
                    </HStack>
                    <FieldPreview field={fl} listsById={listsById} />
                  </Box>
                )
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Modal>
  );
};
